import React, { useEffect, useState } from 'react';
import { Box, Button, InputBase, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { focusRing, meta, pillAction, pressable } from '../../../styles/visualLanguage';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { stepPosition } from '../advisoryStepPosition';
import { normalizeControlCharacters } from '../../../redux/slices/advisory/advisoryTextSanitize';
import type { AdvisoryStage } from '../../../redux/slices/advisory/advisoryTypes';

/**
 * AdvisoryCompanyScreen
 *
 * "Is this the company you're analysing?" — asked the moment the name is read,
 * on the same first turn as the goal, and before the goal is even reviewed.
 *
 * The name is NOT read here. S-02a extracts it from the user's opening question
 * immediately before the goal call and writes it to `facts/company`
 * unconfirmed; this screen settles it. Which is why the confirm action sends
 * `confirmCompany` and, only when the user typed over the proposal,
 * `companyName` — an untouched field means "accept it", and the server records
 * which of the two happened.
 *
 * Asked here rather than at a step before the upload because everything after
 * it is built on it: the engagement type, the question set, and the subject
 * every extracted figure is matched against. A wrong subject does not produce a
 * mislabelled answer, it produces NO answers — every fact is rejected for
 * belonging to a different company — and it does so silently. Correcting it
 * once a plan exists means discarding the plan; correcting it here costs the
 * user one field, while they are still looking at the sentence they typed.
 *
 * Not a counted wizard step: it precedes the fork, so no path exists yet to
 * number it against. It reuses the goal's own '02', the way the fork screen
 * does — the same first-turn decision, not a new one.
 *
 * Structurally the goal-review screen's twin: same `AdvisoryEngagementShell`,
 * same read-only blockquote for the model's proposal, same single `InputBase`
 * for the user's own words, same pill confirm. The difference is that the field
 * here REPLACES the proposal rather than commenting on it, so it is pre-filled
 * with the proposal instead of starting empty.
 *
 * Receives already-derived props and never touches the slice or the supervisor
 * hook itself — `onConfirm` is minted by the caller that owns the write path,
 * exactly as `AdvisoryGoalReviewScreen` requires.
 */
export interface AdvisoryCompanyScreenProps {
  /**
   * The already-narrowed stage. Carries the reading itself, so this screen
   * never touches the slice, the document, or the supervisor hook — the same
   * contract `AdvisoryGoalReviewScreen` follows.
   */
  stage: Extract<AdvisoryStage, { tag: 'awaitingCompanyConfirmation' }>;
  /** True while the confirm turn is in flight; disables both controls. */
  busy?: boolean;
  /** `name` is omitted when the user accepted the proposal untouched. */
  onConfirm: (name?: string) => void;
}

export function AdvisoryCompanyScreen({
  stage,
  busy = false,
  onConfirm,
}: AdvisoryCompanyScreenProps) {
  const { t } = useUntypedTranslation();

  const proposed = stage.companyName.trim();
  const [name, setName] = useState(proposed);

  // The document arrives over a Firestore listener, so the proposal can land
  // after this screen has already mounted (arriving here by URL, or a first
  // turn still settling). Syncing on the proposal's own identity — not on every
  // render — is what lets the field fill in without overwriting a name the user
  // has since typed.
  useEffect(() => {
    setName(proposed);
  }, [proposed]);

  // The goal's own position: this is the tail of the same first-turn
  // decision, and no path exists yet to print a denominator from.
  const position = stepPosition('goal', null);

  // Two different screens wearing one component.
  //
  // With a reading, this CONFIRMS: "is this the company?", with the name set
  // apart to be checked at a glance. With nothing read, there is nothing to
  // confirm and the same words point at an empty space — so it ASKS instead.
  // The user's query genuinely may not name them: "how do we stack up against
  // Puma and Under Armour" says who the competitors are and leaves "we"
  // implicit, which is the most natural way to ask the question and the one
  // case the confirm copy cannot survive.
  const hasProposal = proposed.length > 0;
  const copy = hasProposal ? 'company' : 'companyAsk';

  const sanitized = normalizeControlCharacters(name);
  const trimmed = sanitized.trim();
  const changed = trimmed !== proposed;

  const handleConfirm = () => {
    if (!trimmed || busy) {
      return;
    }
    // Only sends the name when it differs: an untouched field means "accept
    // the reading", and the server keeps the name it already stored. When
    // nothing was read there is no reading to accept, so the typed name is
    // always what travels.
    onConfirm(changed ? trimmed : undefined);
  };

  return (
    <AdvisoryEngagementShell
      position={position?.position ?? '02'}
      total={position?.total ?? null}
      eyebrowKey={`advisory:${copy}.eyebrow`}
      titleKey={`advisory:${copy}.title`}
      hintKey={`advisory:${copy}.hint`}
      actions={
        <>
          <Button
            variant="contained"
            disableRipple
            disabled={trimmed.length === 0 || busy}
            onClick={handleConfirm}
            data-testid="advisory-company-confirm"
            sx={[pillAction, pressable, focusRing]}
          >
            {t(`advisory:${copy}.confirmAction`)}
          </Button>
        </>
      }
    >
      {proposed ? (
        <Typography
          component="blockquote"
          data-testid="advisory-company-proposed"
          aria-label={t('advisory:company.readOnlyAriaLabel')}
          sx={theme => ({
            marginTop: theme.visualPrimitives.goalReadOnlyGap,
            marginLeft: 0,
            marginRight: 0,
            marginBottom: 0,
            fontSize: theme.visualPrimitives.goalReadOnlySize,
            lineHeight: theme.visualPrimitives.goalReadOnlyLineHeight,
            color: theme.colors.inkPrimary,
            borderLeft: `${theme.visualPrimitives.goalReadOnlyBorderWidth} solid ${theme.colors.lavender}`,
            padding: theme.visualPrimitives.goalReadOnlyPadding,
            maxWidth: theme.visualPrimitives.goalReadOnlyMaxWidth,
          })}
        >
          {proposed}
        </Typography>
      ) : (
        <Typography
          data-testid="advisory-company-none"
          sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
        >
          {t(stage.extractionFailed ? 'advisory:company.failedBody' : 'advisory:company.noneBody')}
        </Typography>
      )}

      {hasProposal ? (
        <Typography
          data-testid="advisory-company-caption"
          sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
        >
          {t('advisory:company.caption')}
        </Typography>
      ) : null}

      <Box>
        <InputBase
          fullWidth
          // On `inputProps`, not on the component: MUI spreads `inputProps`
          // onto the inner <input> and puts everything else on the wrapping
          // div, and a test that queries the wrapper cannot read `value`.
          inputProps={{
            'aria-label': t('advisory:company.inputLabel'),
            'data-testid': 'advisory-company-input',
          }}
          placeholder={t('advisory:company.inputPlaceholder')}
          value={name}
          disabled={busy}
          onChange={event => setName(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              handleConfirm();
            }
          }}
          sx={theme => ({
            fontSize: theme.visualPrimitives.inputSize,
            lineHeight: theme.visualPrimitives.inputLineHeight,
            marginTop: theme.visualPrimitives.inputGap,
            color: theme.colors.inkPrimary,
            borderBottom: `1px solid ${theme.visualPrimitives.border}`,
            '& .MuiInputBase-input': {
              padding: theme.visualPrimitives.inputPaddingY,
            },
            '& .MuiInputBase-input::placeholder': {
              color: theme.colors.inkTertiary,
              opacity: 1,
            },
            '&:focus-within': {
              borderBottomColor: theme.colors.lavenderDeep,
            },
          })}
        />
      </Box>
    </AdvisoryEngagementShell>
  );
}

export default AdvisoryCompanyScreen;
